import React, { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Button, Descriptions, Empty, List, Space, Table, Tag, Typography,
} from 'antd';
import { EditOutlined, LinkOutlined } from '@ant-design/icons';

import { servicesAPI } from '../../api/client';
import { ALLOCATION_LABELS, formatDate } from './constants';

const { Text, Title, Link } = Typography;

const PURCHASE_TYPE_LABELS = {
  single: 'Поштучно',
  package: 'Пакет',
  opening_balance: 'Начальный остаток',
};

const ACTION_LABELS = {
  create: 'Создание',
  update: 'Изменение',
  revoke: 'Отзыв',
  assign: 'Выдача',
};


function formatMoney(value, currency) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  const formatted = amount.toLocaleString('ru-RU', { maximumFractionDigits: 4 });
  return `${formatted} ${currency || ''}`.trim();
}

function displayValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function changedFieldsText(changedFields) {
  const entries = Object.entries(changedFields || {});
  if (!entries.length) return ['Без изменений полей'];
  return entries.map(([field, change]) => (
    `${field}: ${displayValue(change?.before)} → ${displayValue(change?.after)}`
  ));
}

export default function ServiceInfoTab({
  service,
  detail,
  accessSummary,
  canEdit,
  onEdit,
  onPurchaseOpen,
}) {
  const serviceId = service?.id;
  const groups = detail?.license_groups || [];
  const purchasedGroups = useMemo(
    () => groups.filter(group => group.license_type === 'purchased'),
    [groups],
  );
  const greyGroups = useMemo(
    () => groups.filter(group => group.license_type === 'grey'),
    [groups],
  );

  const licensingSummary = useMemo(() => {
    const allocationMode = detail?.allocation_mode || 'named_user';
    if (!purchasedGroups.length && greyGroups.length) {
      return {
        mode: ALLOCATION_LABELS[allocationMode] || allocationMode,
        bought: '—',
        used: greyGroups.reduce((sum, group) => sum + Number(group.used_licenses || 0), 0),
        free: '—',
      };
    }

    const bought = purchasedGroups.reduce(
      (sum, group) => sum + Number(group.total_licenses || 0),
      0,
    );
    const used = purchasedGroups.reduce(
      (sum, group) => sum + Number(group.used_licenses || 0),
      0,
    );

    return {
      mode: ALLOCATION_LABELS[allocationMode] || allocationMode,
      bought,
      used,
      free: allocationMode === 'named_user' ? Math.max(bought - used, 0) : '—',
    };
  }, [detail?.allocation_mode, purchasedGroups, greyGroups]);

  const purchaseQueries = useQueries({
    queries: purchasedGroups.map(group => ({
      queryKey: ['license-purchases', group.id],
      queryFn: () => servicesAPI.getLicensePurchases(group.id).then(r => r.data),
      enabled: Boolean(serviceId),
    })),
  });

  const purchases = purchasedGroups.flatMap((_, index) => (
    purchaseQueries[index]?.data || []
  ));
  purchases.sort((a, b) => {
    const bDate = b.purchased_at || b.created_at || '';
    const aDate = a.purchased_at || a.created_at || '';
    return String(bDate).localeCompare(String(aDate));
  });

  const purchaseLoading = purchaseQueries.some(query => query.isLoading);

  const { data: audit } = useQuery({
    queryKey: ['service-audit', serviceId],
    queryFn: () => servicesAPI.getServiceAudit(serviceId, { limit: 100 }).then(r => r.data),
    enabled: Boolean(serviceId),
  });

  const purchaseColumns = [
    {
      title: 'Дата', key: 'date', width: 100,
      render: (_, row) => formatDate(row.purchased_at || row.created_at),
    },
    {
      title: 'Способ покупки',
      dataIndex: 'purchase_type',
      width: 130,
      render: (value) => PURCHASE_TYPE_LABELS[value] || value,
    },
    {
      title: 'Количество',
      key: 'quantity',
      width: 130,
      render: (_, row) => row.purchase_type === 'package'
        ? `${row.package_count} × ${row.licenses_per_package} = ${row.quantity}`
        : row.quantity,
    },
    {
      title: 'Стоимость',
      key: 'cost',
      width: 130,
      render: (_, row) => formatMoney(row.total_cost, row.currency),
    },
    {
      title: 'Договор',
      dataIndex: 'contract_url',
      key: 'contract_url',
      width: 100,
      render: (value) => value
        ? (
          <Link
            href={value}
            target="_blank"
            onClick={(event) => event.stopPropagation()}
          >
            Открыть
          </Link>
        )
        : '—',
    },
    {
      title: 'Закупка',
      dataIndex: 'purchase_url',
      key: 'purchase_url',
      width: 100,
      render: (value) => value
        ? (
          <Link
            href={value}
            target="_blank"
            onClick={(event) => event.stopPropagation()}
          >
            Открыть
          </Link>
        )
        : '—',
    },
  ];

  if (!detail) return <Empty description="Данные сервиса загружаются" />;

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>Основная информация</Title>
          {canEdit && (
            <Button size="small" icon={<EditOutlined />} onClick={onEdit}>
              Редактировать
            </Button>
          )}
        </Space>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Название">{detail.name}</Descriptions.Item>
          <Descriptions.Item label="Категория">
            {detail.category_name
              ? <Tag color={detail.category_color}>{detail.category_name}</Tag>
              : 'Без категории'}
          </Descriptions.Item>
          <Descriptions.Item label="Поставщик">{displayValue(detail.vendor)}</Descriptions.Item>
          <Descriptions.Item label="Статус">
            {detail.is_active ? <Tag color="green">Активен</Tag> : <Tag>Неактивен</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Сайт" span={2}>
            {detail.website
              ? <Link href={detail.website} target="_blank" icon={<LinkOutlined />}>{detail.website}</Link>
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="1Password" span={2}>
            {detail.onepassword_link
              ? <Link href={detail.onepassword_link} target="_blank" icon={<LinkOutlined />}>Открыть запись</Link>
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Примечание" span={2}>{displayValue(detail.notes)}</Descriptions.Item>
        </Descriptions>
      </div>

      <div>
        <Title level={5}>Лицензирование</Title>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Тип лицензирования">{licensingSummary.mode}</Descriptions.Item>
          <Descriptions.Item label="Куплено">{licensingSummary.bought}</Descriptions.Item>
          <Descriptions.Item label="Выдано">{licensingSummary.used}</Descriptions.Item>
          <Descriptions.Item label="Свободно">{licensingSummary.free}</Descriptions.Item>
          <Descriptions.Item label="Грязный домен">{Number(accessSummary?.greyUsers || 0)}</Descriptions.Item>
          <Descriptions.Item label="Пользователей всего">{Number(accessSummary?.totalUsers || 0)}</Descriptions.Item>
        </Descriptions>
      </div>

      <div>
        <Title level={5} style={{ marginBottom: 4 }}>История приобретений</Title>
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
        >
          Нажмите на запись, чтобы открыть или изменить данные
        </Text>
        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <Table
            rowKey="id"
            size="small"
            loading={purchaseLoading}
            pagination={{ pageSize: 10 }}
            dataSource={purchases}
            columns={purchaseColumns}
            locale={{ emptyText: 'Нет приобретений' }}
            onRow={(row) => ({
              onClick: () => onPurchaseOpen?.(row),
              style: {
                cursor: canEdit && onPurchaseOpen ? 'pointer' : 'default',
              },
            })}
          />
        </div>
      </div>

      <div>
        <Title level={5}>История изменений</Title>
        {!audit?.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="История изменений пуста" />
        ) : (
          <List
            size="small"
            dataSource={audit}
            renderItem={(event) => (
              <List.Item>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Space wrap>
                    <Text strong>{event.actor_name || event.actor_login}</Text>
                    <Tag>{ACTION_LABELS[event.action] || event.action}</Tag>
                    <Text type="secondary">{formatDate(event.created_at)}</Text>
                  </Space>
                  {changedFieldsText(event.changed_fields).map((line) => (
                    <Text key={line} type="secondary" style={{ fontSize: 12 }}>{line}</Text>
                  ))}
                </Space>
              </List.Item>
            )}
          />
        )}
      </div>
    </Space>
  );
}
